/**
 * Recency encoding (memory tier 2): `updated_at` becomes a subtle visual —
 * a relative chip in the detail pane ("updated 3d ago") and node opacity by
 * recency quartile WITHIN the loaded nodes. Two rules are load-bearing:
 *
 * 1. Quartiles are computed over the records actually loaded in the view,
 *    never over absolute dates — a canvas of last week's records must not
 *    render all-fresh or all-stale because of what the clock says elsewhere.
 *    (Absolute dates ON nodes are clutter by design; the legend explains the
 *    encoding instead.)
 * 2. A record with no known `updated_at` (a neighbours projection carries
 *    none; a search row replaced it with the rank) is NEUTRAL — it neither
 *    brightens nor dims, and it does not participate in the quartile cut.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * "updated 3d ago" — the relative age of one timestamp, coarse by design
 * (minutes → hours → days). Returns null for an absent/unparseable
 * timestamp: no chip is better than a wrong chip.
 */
export function relativeUpdated(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const age = Math.max(0, now - ts);
  if (age < MINUTE_MS) return 'updated just now';
  if (age < HOUR_MS) return `updated ${Math.floor(age / MINUTE_MS)}m ago`;
  if (age < DAY_MS) return `updated ${Math.floor(age / HOUR_MS)}h ago`;
  return `updated ${Math.floor(age / DAY_MS)}d ago`;
}

/** Opacity per recency quartile, OLDEST (q0) → NEWEST (q3). The spacing is
 *  deliberate: visibly graded without hiding the oldest records — opacity
 *  modulates the privacy tint, it never replaces it. */
export const RECENCY_QUARTILE_OPACITY = [0.5, 0.7, 0.85, 1] as const;

/** The quartile cut itself: `ceil(rank * 4 / n) - 1`, clamped to 0..3. A
 *  single timestamped node is q3 (the freshest thing in the view IS the
 *  freshest), two split q1/q3, four split evenly. */
function quartile(index: number, count: number): number {
  return Math.min(3, Math.max(0, Math.ceil(((index + 1) * 4) / count) - 1));
}

/**
 * The opacity each LOADED node carries, by recency quartile among the nodes
 * that HAVE an `updated_at`. Ids without a timestamp map to undefined — the
 * caller leaves those untouched (neutral), never guesses them dim or fresh.
 */
export function recencyOpacityById(
  nodes: Array<{ id: string; updatedAt?: string | null }>,
): Map<string, number> {
  const stamped = nodes
    .filter((n) => n.updatedAt !== null && n.updatedAt !== undefined && n.updatedAt !== '')
    .map((n) => ({ id: n.id, ts: Date.parse(n.updatedAt!) }))
    .filter((n) => !Number.isNaN(n.ts))
    // Oldest first: index 0 is the stale end of the view.
    .sort((a, b) => a.ts - b.ts);
  const out = new Map<string, number>();
  stamped.forEach((n, i) => {
    out.set(n.id, RECENCY_QUARTILE_OPACITY[quartile(i, stamped.length)]!);
  });
  return out;
}
